/**
 * Base Process Class
 * 
 * Provides common functionality for all processes:
 * - Resource tracking (event listeners, timers, intervals)
 * - Standard lifecycle management
 * - Common view creation patterns
 * - Event listener cleanup
 * 
 * All processes should extend this class instead of implementing Process directly.
 */

import type { Process, ProcessContext, ViewInstance } from './process.js';
import { ProcessPriority, ProcessLifecycleMode } from './process.js';
import { BaseViewHTML } from './base-view-html.js';
import { bus } from '../core/events.js';

/**
 * Base class for all processes
 * 
 * Provides:
 * - Automatic resource tracking and cleanup
 * - Standard view creation helpers
 * - Common lifecycle patterns
 * 
 * Usage:
 * ```typescript
 * export class MyProcess extends BaseProcess {
 *   constructor(id: string) {
 *     super(id, 'my-process');
 *   }
 * 
 *   start(ctx: ProcessContext): void {
 *     super.start(ctx); // Initialize base functionality
 *     
 *     // Create standard square view with dismiss button
 *     const view = this.createStandardSquareView(ctx, {
 *       preferredSize: { width: 400, height: 600 },
 *       getHTML: () => this.renderHTML()
 *     });
 *     
 *     ctx.registerView(view);
 *   }
 * }
 * ```
 */
export abstract class BaseProcess implements Process {
  public readonly id: string;
  public abstract readonly kind: string;

  public metadata: {
    priority: ProcessPriority;
    category: 'system' | 'communication' | 'productivity' | 'entertainment' | 'utility' | 'demo';
    canStop: boolean;
    preserveState: boolean;
    timeSensitive: boolean;
  } = {
    priority: ProcessPriority.NORMAL,
    category: 'utility',
    canStop: true,
    preserveState: false,
    timeSensitive: false
  };

  protected context: ProcessContext | null = null;
  protected views: Map<string, ViewInstance> = new Map();

  constructor(id: string) {
    this.id = id;
  }

  /**
   * Start the process (called by ProcessManager)
   * 
   * Subclasses should override this and call super.start(ctx) first.
   */
  start(ctx: ProcessContext): void {
    this.context = ctx;
    this.onStart(ctx);
  }

  /**
   * Hook for subclasses to implement their start logic
   */
  protected abstract onStart(ctx: ProcessContext): void;

  /**
   * Stop the process (cleanup)
   * 
   * Automatically cleans up all tracked resources.
   */
  stop?(): void {
    if (this.onStop) {
      this.onStop();
    }
    this.cleanup();
  }

  /**
   * Hook for subclasses to implement their stop logic
   */
  protected onStop?(): void;

  /**
   * Cleanup all resources
   */
  protected cleanup(): void {
    // Cleanup is handled by ProcessContext, but we can add process-specific cleanup here
    this.views.clear();
    this.context = null;
  }

  /**
   * Create a standard square view with common features
   * 
   * This handles:
   * - Standard metadata (lifecycle, allocation, size constraints)
   * - Dismiss button (if showDismissButton is true)
   * - Common view structure
   * 
   * @param ctx Process context
   * @param options View creation options
   * @returns Configured ViewInstance
   */
  protected createStandardSquareView(
    ctx: ProcessContext,
    options: {
      viewId?: string;
      preferredSize: { width: number; height: number };
      minSize?: { width: number; height: number };
      maxSize?: { width: number; height: number };
      preferredRatio?: number;
      showDismissButton?: boolean;
      canCollapse?: boolean;
      collapsedSize?: { width: number; height: number };
      getHTML?: () => string;
      render?: (ctx: CanvasRenderingContext2D | WebGLRenderingContext) => void;
      data?: unknown;
    }
  ): ViewInstance {
    const viewId = options.viewId || `${this.id}-main`;
    const preferredSize = options.preferredSize;
    const minSize = options.minSize || { width: preferredSize.width * 0.5, height: preferredSize.height * 0.5 };
    const maxSize = options.maxSize || { width: preferredSize.width * 2, height: preferredSize.height * 2 };
    const preferredRatio = options.preferredRatio ?? (preferredSize.width / preferredSize.height);

    const view: ViewInstance = {
      id: viewId,
      kind: 'square',
      processId: this.id,
      geometry: {
        x: 0,
        y: 0,
        width: preferredSize.width,
        height: preferredSize.height
      },
      metadata: {
        showDismissButton: options.showDismissButton ?? true, // Default: all square views have dismiss button
        sizeConstraints: {
          // Convert pixel sizes to cell-based acceptableSizes
          acceptableSizes: [
            { cols: Math.ceil(preferredSize.width / 200), rows: Math.ceil(preferredSize.height / 200) }, // Preferred
            { cols: Math.ceil(minSize.width / 200), rows: Math.ceil(minSize.height / 200) } // Minimum
          ]
        }
      },
      data: options.data || (options.getHTML ? {
        getHTML: options.getHTML
      } : undefined),
      render: options.render,
      destroy: () => {
        // Standard cleanup - subclasses can override by providing custom destroy
        this.views.delete(viewId);
      }
    };

    this.views.set(viewId, view);
    return view;
  }

  /**
   * Create a headless view (no UI, for background processes)
   */
  protected createHeadlessView(ctx: ProcessContext, viewId?: string): ViewInstance {
    const id = viewId || `${this.id}-headless`;

    const view: ViewInstance = {
      id,
      kind: 'none',
      processId: this.id,
      metadata: {
        showDismissButton: false,
        sizeConstraints: {
          acceptableSizes: [{ cols: 0, rows: 0 }] // Headless - no size
        }
      },
      destroy: () => {
        this.views.delete(id);
      }
    };

    this.views.set(id, view);
    return view;
  }

  /**
   * Get a view by ID
   */
  protected getView(viewId: string): ViewInstance | undefined {
    return this.views.get(viewId);
  }

  /**
   * Create a view from a BaseView class instance
   * 
   * This helper method allows processes to easily use BaseView subclasses
   * by converting them to ViewInstance objects.
   * 
   * Usage:
   * ```typescript
   * const myView = new MyView(this.id, options);
   * const viewInstance = this.createViewFromBaseView(myView);
   * ctx.registerView(viewInstance);
   * ```
   */
  protected createViewFromBaseView(baseView: BaseViewHTML): ViewInstance {
    const viewInstance = baseView.toViewInstance();
    this.views.set(viewInstance.id, viewInstance);
    return viewInstance;
  }

  /**
   * Update a view's HTML content
   */
  protected updateViewHTML(viewId: string, getHTML: () => string): void {
    const view = this.views.get(viewId);
    if (view && view.data) {
      (view.data as any).getHTML = getHTML;
      bus.emit('process.view.updated', {
        processId: this.id,
        viewId: view.id
      });
    }
  }

  /**
   * Register an event listener (automatically tracked for cleanup)
   */
  protected onEvent(event: string, handler: (payload: unknown) => void): void {
    if (this.context) {
      this.context.trackEventListener?.(event, handler);
      bus.on(event, handler);
    }
  }

  /**
   * Track a timer (automatically cleaned up on stop)
   */
  protected trackTimer(timerId: number): void {
    if (this.context) {
      this.context.trackTimer?.(timerId);
    }
  }

  /**
   * Track an interval (automatically cleaned up on stop)
   */
  protected trackInterval(intervalId: number): void {
    if (this.context) {
      this.context.trackInterval?.(intervalId);
    }
  }
}

